import React, { useContext } from 'react';
import _get from 'lodash/get';
import { v4 } from 'uuid';

const concat = (...strings) => {
  if (strings.some((str) => React.isValidElement(str))) {
    return (
      <>
        {strings.map((str) => (
          <React.Fragment key={v4()}>{str}</React.Fragment>
        ))}
      </>
    );
  }
  return strings.join('');
};

/**
 * Recursively constructs JSX array containing interpolated JSX content
 * Automatically degrades to plaintext string if possible
 * @param {string} str - TTP string to format.
 * @param {object} substrings - Object properties are used for substitution. Values are substituted into message based on key.
 * @returns {(string|React.Component)} - Formatted string or JSX
 */
export const formatTtp = (str = '', substrings = {}) => {
  let replaceStr;
  // Replace with matchAll or use matchAll polyfill in the future,
  // if custom regex is ever supported
  const [placeholder, nextPlaceholder] = (str.match(/##(.*?)##/g) || []).map((key) =>
    key.slice(2, key.length - 2)
  );

  // When '##SUBSTRING##' or '##%STRING_KEY##SUBSTRING##%##' syntax detected
  if (placeholder) {
    let tailString;
    const splitString = str.split(/##.*?##(.+)?/);

    // When using '##%STRING_KEY##SUBSTRING##%##' syntax in TTP string'
    if (placeholder[0] === '%' && nextPlaceholder === '%') {
      let subText = null;
      [subText, tailString = ''] = (splitString[1] || '').split(/##.*?##(.+)?/);
      const param = substrings[placeholder.substr(1)];
      replaceStr = typeof param === 'function' ? param(subText) : param;
    } else {
      const param = substrings[placeholder];
      replaceStr = typeof param === 'function' ? param() : param;
    }

    // Concatenate the pieces back together
    return concat(
      splitString[0],
      replaceStr !== undefined ? replaceStr : '%%MISSING STRING%%',
      formatTtp(tailString !== undefined ? tailString : splitString[1], substrings)
    );
  }
  return str;
};

/**
 * Fetch TTP string from object
 * @param {object} src - Object to fetch TTP string from
 * @param {string|Array.<string>} id - Path to string in TTP context object
 * @param {object|Array.<object>} substrings - Object properties are used for substitution. Values are substituted into message based on key.
 * @returns {(string|React.Component)} - Formatted string or JSX
 */
export const fetchTtp = (src, id, substrings) => {
  if (Array.isArray(id)) {
    return id.map((x, idx) =>
      formatTtp(_get(src, x), Array.isArray(substrings) ? substrings[idx] : substrings)
    );
  }
  return formatTtp(_get(src, id), Array.isArray(substrings) ? substrings[0] : substrings);
};

/**
 * React context for TTP messages
 */
export const TtpContext = React.createContext({});

/**
 * React hook that returns function that fetches TTP strings
 * @returns {(id: string|Array.<string>, substrings: (object|Array.<object>)) => (string|React.Component)} - Function to fetch TTP strings
 */
export const useTtp = () => {
  const context = useContext(TtpContext);
  return (id, substrings) => fetchTtp(context, id, substrings);
};
